import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance,
  AttachMoney,
  CreditCard,
  Schedule,
  TrendingUp,
  Settings,
  CheckCircle,
  Event,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { parseDate } from '../utils/dateUtils';
import { partnerService } from '../services/api';
import NotificationSnackbar from './NotificationSnackbar';

interface Partner {
  id: string;
  partnerType?: 'MERCHANT' | 'RETAILER' | 'AGENT';
  balance?: number;
  floatBalance?: number;
  settlementFrequency: string;
  settlementDelayDays?: number;
  defaultCommissionRate: number;
  specialCommissionRate?: number;
  bankAccount?: string;
  transactionLimitDaily?: number;
  transactionLimitMonthly?: number;
}

interface PartnerAccountProps {
  partner: Partner;
}

const PartnerAccount: React.FC<PartnerAccountProps> = ({ partner }) => {
  // Bank Accounts State
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(true);

  // Banking Details Dialog
  const [bankingDialogOpen, setBankingDialogOpen] = useState(false);
  const [bankingDetails, setBankingDetails] = useState({
    bankName: '',
    accountNumber: '',
    accountName: '',
    branchCode: '',
    swiftCode: '',
    currency: 'USD',
    isPrimary: true,
  });

  // Limit Increase Dialog
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitRequest, setLimitRequest] = useState({
    limitType: 'DAILY_TRANSACTION' as 'DAILY_TRANSACTION' | 'MONTHLY_TRANSACTION' | 'SINGLE_TRANSACTION',
    requestedLimit: 0,
    currentLimit: 0,
    justification: '',
    businessReason: '',
  });

  // Settlement Schedule Dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [settlementSchedule, setSettlementSchedule] = useState<any>(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  // Notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, type });
  };

  // Fetch bank accounts on mount
  useEffect(() => {
    fetchBankAccounts();
  }, [partner.id]);

  const fetchBankAccounts = async () => {
    setLoadingBankAccounts(true);
    try {
      const response = await partnerService.getBankAccounts(partner.id);
      setBankAccounts(response);
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  const handleUpdateBanking = async () => {
    try {
      await partnerService.updateBankingDetails(partner.id, bankingDetails);
      showNotification('Banking details updated successfully', 'success');
      setBankingDialogOpen(false);
      setBankingDetails({
        bankName: '',
        accountNumber: '',
        accountName: '',
        branchCode: '',
        swiftCode: '',
        currency: 'USD',
        isPrimary: true,
      });
      // Refresh bank accounts list
      fetchBankAccounts();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to update banking details', 'error');
    }
  };

  const handleRequestLimitIncrease = async () => {
    if (!limitRequest.justification || !limitRequest.businessReason) {
      showNotification('Please provide justification and business reason', 'warning');
      return;
    }

    try {
      const response = await partnerService.requestLimitIncrease(partner.id, limitRequest);
      showNotification(
        `Limit increase request submitted successfully. Request ID: ${response.requestId}`,
        'success'
      );
      setLimitDialogOpen(false);
      setLimitRequest({
        limitType: 'DAILY_TRANSACTION',
        requestedLimit: 0,
        currentLimit: 0,
        justification: '',
        businessReason: '',
      });
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to submit limit increase request', 'error');
    }
  };

  const handleViewSchedule = async () => {
    setScheduleDialogOpen(true);
    setLoadingSchedule(true);
    try {
      const response = await partnerService.getSettlementSchedule(partner.id);
      setSettlementSchedule(response);
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to load settlement schedule', 'error');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const openLimitDialog = (type: 'DAILY_TRANSACTION' | 'MONTHLY_TRANSACTION' | 'SINGLE_TRANSACTION') => {
    setLimitRequest({
      ...limitRequest,
      limitType: type,
      currentLimit: type === 'DAILY_TRANSACTION'
        ? (partner.transactionLimitDaily || 0)
        : (partner.transactionLimitMonthly || 0),
    });
    setLimitDialogOpen(true);
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Account Balances */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Balances
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="caption">
                          Current Balance
                        </Typography>
                        <Typography variant="h4">
                          ${partner.balance?.toLocaleString() || '0.00'}
                        </Typography>
                      </Box>
                      <AttachMoney color="primary" fontSize="large" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              {partner.partnerType === 'AGENT' ? (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<CreditCard />}>
                    Agent float is managed in the external float system. Collections are
                    recorded against the shared Agent Pool account.
                  </Alert>
                </Grid>
              ) : (
                partner.floatBalance !== undefined && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography color="textSecondary" gutterBottom variant="caption">
                              Float Balance
                            </Typography>
                            <Typography variant="h4">
                              ${partner.floatBalance.toLocaleString()}
                            </Typography>
                            {partner.floatBalance < 1000 && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                Low float balance. Please top up to continue operations.
                              </Alert>
                            )}
                          </Box>
                          <CreditCard color="info" fontSize="large" />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Settlement Configuration */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Settlement Configuration
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Schedule />
                </ListItemIcon>
                <ListItemText
                  primary="Settlement Frequency"
                  secondary={partner.settlementFrequency}
                />
              </ListItem>
              {partner.settlementDelayDays && (
                <ListItem>
                  <ListItemIcon>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText
                    primary="Settlement Delay"
                    secondary={`${partner.settlementDelayDays} days`}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <TrendingUp />
                </ListItemIcon>
                <ListItemText
                  primary="Commission Rate"
                  secondary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">
                        {partner.defaultCommissionRate}%
                      </Typography>
                      {partner.specialCommissionRate && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            (Special: {partner.specialCommissionRate}%)
                          </Typography>
                          <Chip label="Custom Rate" size="small" color="secondary" />
                        </>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Banking Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Banking Information
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => setBankingDialogOpen(true)}
                size="small"
              >
                Add/Update Bank Account
              </Button>
            </Box>

            {loadingBankAccounts ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : bankAccounts.length === 0 ? (
              <Alert severity="warning">
                No bank account configured. Settlements will be held until banking details are provided.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bank Name</TableCell>
                      <TableCell>Account Number</TableCell>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Branch Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Primary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bankAccounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.bankName}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {account.accountNumber}
                        </TableCell>
                        <TableCell>{account.accountName}</TableCell>
                        <TableCell>{account.currency}</TableCell>
                        <TableCell>{account.branchCode || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={account.verificationStatus || 'PENDING'}
                            color={
                              account.verificationStatus === 'VERIFIED'
                                ? 'success'
                                : account.verificationStatus === 'REJECTED'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {account.isPrimary && (
                            <Chip
                              icon={<CheckCircle />}
                              label="Primary"
                              color="primary"
                              size="small"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Transaction Limits */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Transaction Limits
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Daily Transaction Limit
                  </Typography>
                  <Typography variant="h5">
                    ${partner.transactionLimitDaily?.toLocaleString() || 'Unlimited'}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => openLimitDialog('DAILY_TRANSACTION')}
                  >
                    Request Increase
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Monthly Transaction Limit
                  </Typography>
                  <Typography variant="h5">
                    ${partner.transactionLimitMonthly?.toLocaleString() || 'Unlimited'}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => openLimitDialog('MONTHLY_TRANSACTION')}
                  >
                    Request Increase
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Event />}
              onClick={handleViewSchedule}
            >
              View Settlement Schedule
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Update Banking Details Dialog */}
      <Dialog open={bankingDialogOpen} onClose={() => setBankingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Banking Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Name"
                value={bankingDetails.bankName}
                onChange={(e) => setBankingDetails({ ...bankingDetails, bankName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Number"
                value={bankingDetails.accountNumber}
                onChange={(e) => setBankingDetails({ ...bankingDetails, accountNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Name"
                value={bankingDetails.accountName}
                onChange={(e) => setBankingDetails({ ...bankingDetails, accountName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={bankingDetails.currency}
                  label="Currency"
                  onChange={(e) => setBankingDetails({ ...bankingDetails, currency: e.target.value })}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="ZWG">ZWG</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Branch Code"
                value={bankingDetails.branchCode}
                onChange={(e) => setBankingDetails({ ...bankingDetails, branchCode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SWIFT Code"
                value={bankingDetails.swiftCode}
                onChange={(e) => setBankingDetails({ ...bankingDetails, swiftCode: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankingDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateBanking} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Limit Increase Dialog */}
      <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Limit Increase</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Limit Type</InputLabel>
                <Select
                  value={limitRequest.limitType}
                  label="Limit Type"
                  onChange={(e) => setLimitRequest({ ...limitRequest, limitType: e.target.value as any })}
                >
                  <MenuItem value="DAILY_TRANSACTION">Daily Transaction Limit</MenuItem>
                  <MenuItem value="MONTHLY_TRANSACTION">Monthly Transaction Limit</MenuItem>
                  <MenuItem value="SINGLE_TRANSACTION">Single Transaction Limit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Limit"
                type="number"
                value={limitRequest.currentLimit}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Requested Limit"
                type="number"
                value={limitRequest.requestedLimit}
                onChange={(e) => setLimitRequest({ ...limitRequest, requestedLimit: parseFloat(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Justification"
                multiline
                rows={3}
                value={limitRequest.justification}
                onChange={(e) => setLimitRequest({ ...limitRequest, justification: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Reason"
                multiline
                rows={3}
                value={limitRequest.businessReason}
                onChange={(e) => setLimitRequest({ ...limitRequest, businessReason: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRequestLimitIncrease} variant="contained">
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settlement Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Settlement Schedule</DialogTitle>
        <DialogContent>
          {loadingSchedule ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Loading schedule...</Typography>
            </Box>
          ) : settlementSchedule ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Settlement Frequency
                  </Typography>
                  <Typography variant="body1">{settlementSchedule.settlementFrequency}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Settlement Time
                  </Typography>
                  <Typography variant="body1">{settlementSchedule.settlementTime || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Currency
                  </Typography>
                  <Typography variant="body1">{settlementSchedule.currency}</Typography>
                </Grid>
                {settlementSchedule.bankAccount && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Primary Bank Account
                    </Typography>
                    <Typography variant="body1">
                      {settlementSchedule.bankAccount.bankName} - {settlementSchedule.bankAccount.accountNumber}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="h6" gutterBottom>
                Upcoming Settlements (Next 7 Days)
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settlementSchedule.upcomingSettlements?.map((settlement: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{format(parseDate(settlement.settlementDate), 'PPP')}</TableCell>
                        <TableCell>{settlement.settlementTime || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip label={settlement.status} size="small" color="primary" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default PartnerAccount;
